// Template prompts and responses data
const templateData = {
  // Text templates
  text: {
    seo_description: {
      prompt: `You are a conversion copywriter.

Rewrite the following product description to be more persuasive and appealing to online shoppers.

Use these inputs:
- Title: {title}
- Original Description: {description}
- Target Keywords: {custom.googlekeywords}

Your version should:
- Focus on benefits, not just features
- Be scannable and easy to read
- Naturally include relevant keywords
- Keep it under 150 words`,
      inputFields: [
        { id: 'title', label: 'Title', value: 'Wireless Bluetooth Earbuds' },
        { id: 'description', label: 'Description', value: 'These compact earbuds deliver crisp sound, long battery life, and a sleek matte black finish. Compatible with iOS and Android devices.' },
        { id: 'custom.googlekeywords', label: 'Custom Metafield', value: 'bluetooth earbuds, wireless headphones, noise cancelling' }
      ],
      responses: {
        openai: `Experience true freedom with these wireless Bluetooth earbuds — designed for crisp, immersive sound wherever you go.

With a long-lasting battery, you can enjoy your favorite music or podcasts without interruptions. The sleek matte black finish adds a modern touch, while the compact design fits comfortably for all-day wear.

Whether you're using an iPhone or Android, these noise cancelling wireless headphones pair effortlessly for seamless listening on any device. Perfect for commuting, workouts, or relaxing at home.`,
        deepseek: `Enjoy crisp, immersive sound with these sleek, matte black earbuds—perfect for music, calls, and more. With long battery life, they keep up with your day, while the compact, lightweight design ensures all-day comfort.

✅ Universal Compatibility – Works seamlessly with iOS & Android
✅ Noise Isolation – Minimizes distractions for clearer audio
✅ Wireless Freedom – Bluetooth connectivity with no tangled wires

Ideal for workouts, commutes, or just relaxing—these earbuds deliver premium sound without the premium price.`,
        gemini: `Escape the Wires & Elevate Your Sound

Tired of tangled cords? Experience true listening freedom with our sleek Wireless Bluetooth Earbuds. Designed for your busy life, these compact earbuds deliver stunningly crisp sound that brings your music and calls to life.

Why you'll love them:

• Listen Longer: Power through your day with impressive, long-lasting battery life. Less charging, more jamming!
• Immersive Audio: Enjoy rich, clear sound quality for your favorite playlists, podcasts, and important calls.
• Seamless Connection: Effortlessly pair with your iOS or Android devices for instant, reliable wireless audio.
• Sleek Style: Look sharp with the modern, matte black finish that feels as good as it looks.

Ready for an upgrade? Grab these essential Bluetooth earbuds and enjoy incredible sound without the hassle.`
      }
    },
    
    specifications: {
      prompt: `You are a product data assistant.

Based on the product information below:
- Title: {title}
- Description: {description}

Extract a list of technical or practical specifications in bullet points. Focus on measurable attributes, materials, compatibility, dimensions, or special features.

Avoid marketing language — make it clean, clear, and factual.`,
      inputFields: [
        { id: 'title', label: 'Title', value: 'Wireless Bluetooth Earbuds' },
        { id: 'description', label: 'Description', value: 'These compact earbuds deliver crisp sound, long battery life, and a sleek matte black finish. Compatible with iOS and Android devices.' }
      ],
      responses: {
        openai: `Technical Specifications:

• Wireless connectivity: Bluetooth

• Compatibility: iOS and Android devices

• Battery life: Long-lasting (exact duration not specified)

• Finish: Matte black

• Form factor: Compact earbuds`,
        deepseek: `Technical Specifications:
• Connectivity: Wireless Bluetooth

• Battery Life: Long (exact hours not specified)

• Sound Quality: Crisp audio output

• Finish: Matte black

• Compatibility: iOS & Android devices

• Size: Compact (dimensions not specified)`,
        gemini: `Connectivity: Bluetooth (Wireless)
Compatibility: iOS and Android devices
Color/Finish: Matte Black
Feature: Long battery life
Type: Earbuds`
      }
    },
    
    seo_title: {
      prompt: `You are an e-commerce SEO assistant.

Based on the following information:
- Current Title: {title}
- Product Description: {description}
- Target Keywords: {custom.googlekeywords}

Generate a new SEO-optimized product title that:
- Is clear, specific, and relevant to search engines
- Includes one or more of the provided keywords naturally
- Stays under 70 characters
- Sounds appealing to potential customers`,
      inputFields: [
        { id: 'title', label: 'Title', value: 'Wireless Bluetooth Earbuds' },
        { id: 'description', label: 'Description', value: 'These compact earbuds deliver crisp sound, long battery life, and a sleek matte black finish. Compatible with iOS and Android devices.' },
        { id: 'custom.googlekeywords', label: 'Custom Metafield', value: 'bluetooth earbuds, wireless headphones, noise cancelling' }
      ],
      responses: {
        openai: 'Wireless Bluetooth Earbuds – Noise Cancelling Headphones for iOS & Android',
        deepseek: 'Wireless Bluetooth Earbuds – Noise Cancelling, Long Battery Life',
        gemini: 'Matte Black Wireless Bluetooth Earbuds - Long Battery'
      }
    },
    
    image_description: {
      prompt: `You are a professional e-commerce copywriter.

Write a rich product description for:
- Title: {title}
- Description: {description}

Place product images where they best complement the text. Use {{IMAGE_NEXT}} placeholders to embed images between sections.

Your description should:
- Be visually rich with embedded product images
- Use HTML formatting (headings, paragraphs, lists)
- Place images between sections for visual appeal
- Focus on benefits and features
- Be 200-300 words`,
      inputFields: [
        { id: 'title', label: 'Title', value: 'Wireless Bluetooth Earbuds' },
        { id: 'description', label: 'Description', value: 'These compact earbuds deliver crisp sound, long battery life, and a sleek matte black finish.' }
      ],
      responses: {
        openai: `<h2>Experience Pure Sound Freedom</h2>
<p>Discover the perfect blend of style and performance with our Wireless Bluetooth Earbuds.</p>

{{IMAGE_NEXT}}

<h3>Crystal Clear Audio</h3>
<p>Enjoy crisp, immersive sound quality that brings your music to life. Whether you're on a call or listening to your favorite playlist, every note comes through with stunning clarity.</p>

{{IMAGE_NEXT}}

<h3>Built for Your Lifestyle</h3>
<ul>
<li>Long-lasting battery for all-day listening</li>
<li>Sleek matte black finish</li>
<li>Compatible with iOS and Android</li>
</ul>`,
        deepseek: `<h2>Wireless Bluetooth Earbuds</h2>

{{IMAGE_NEXT}}

<p>Step into the future of audio with our premium wireless earbuds. Designed for comfort and built for performance.</p>

<h3>Key Features</h3>
<ul>
<li>✅ Crisp, clear sound</li>
<li>✅ Extended battery life</li>
<li>✅ Universal compatibility</li>
</ul>

{{IMAGE_NEXT}}

<p>Order now and experience the difference.</p>`,
        gemini: `<h2>Your New Favorite Earbuds</h2>

{{IMAGE_NEXT}}

<p>Say goodbye to tangled wires and hello to freedom. Our Wireless Bluetooth Earbuds deliver premium sound in a sleek, compact package.</p>

<h3>Why You'll Love Them</h3>
<p>From the matte black finish to the all-day battery life, every detail is designed with you in mind.</p>

{{IMAGE_NEXT}}

<p>Compatible with all your devices. Ready when you are.</p>`
      }
    }
  },
  
  // Image templates
  clothing: {
    prompt: 'Describe the clothing item in the image: style, material, color, fit, and visible features or accessories.',
    responses: {
      openai: 
        'This is an elegant baby or toddler girl\'s dress featuring a sleeveless black ribbed top with decorative buttons down the front and ruffled detailing on the shoulders.\n\n' +
        'The dress includes a wide satin ribbon bow at the waist in matching black.\n\n' +
        'The skirt is a soft, layered tulle in a light beige or blush tone, adorned with playful black polka dots for a charming contrast.\n\n' +
        'The overall style is dressy and playful—ideal for parties or special occasions.\n\n' +
        'The fit appears to be comfortable and flared at the bottom, giving a lovely princess-like silhouette.',

      gemini: 
        '1. Item: Child\'s Dress (likely for a toddler or young girl).\n\n' +
        '2. Style:\n' +
        '  • Fit-and-flare silhouette\n' +
        '  • Mixed-media design with knit bodice + tulle skirt\n' +
        '  • Sleeveless with cap-sleeve-style ruffles\n' +
        '  • Suitable for parties or special occasions\n\n' +
        '3. Material:\n' +
        '  • Bodice: Ribbed black knit (likely cotton blend)\n' +
        '  • Skirt: Sheer tulle with black polka dots over pale pink lining\n' +
        '  • Bow: Satin or grosgrain ribbon\n\n' +
        '4. Color:\n' +
        '  • Bodice: Black\n' +
        '  • Skirt: Light pink with black dots\n' +
        '  • Bow: Black\n' +
        '  • Buttons: Metallic (gold or silver tone)\n\n' +
        '5. Fit:\n' +
        '  • Fitted bodice\n' +
        '  • Flared skirt (likely knee-length)\n\n' +
        '6. Features:\n' +
        '  • Decorative buttons\n' +
        '  • Layered ruffles\n' +
        '  • Large waist bow\n' +
        '  • Multi-layer tulle\n' +
        '  • Ribbed texture\n' +
        '  • Round neckline'
    }
  },
  tech: {
    prompt: 'Analyze the tech product in the image: type, key features, design, materials, and visible ports or buttons.',
    responses: {
      openai: 
        'This is an LED crystal lamp featuring a spherical glass design with an etched 3D illusion of jellyfish, stars, the moon, and a cloud, all illuminated from the base.\n\n' +
        '• Type: LED decorative night lamp\n\n' +
        '• Key Features: 3D laser-engraved jellyfish and celestial imagery, soft ambient blue lighting, USB-powered\n\n' +
        '• Design: Globe-shaped clear crystal orb on a circular wooden base; visually stunning and modern aesthetic\n\n' +
        '• Materials: Glass/crystal (orb), wood or wood-like material (base)\n\n' +
        '• Ports/Buttons: A visible USB cable connected to the base suggests it’s USB-powered, but no external buttons are immediately visible (may be touch-sensitive or remote-controlled)',

      gemini: 
        '1. Product Type: LED Crystal Ball Night Light\n\n' +
        '2. Key Features:\n' +
        '  • 3D laser-engraved jellyfish, moon, and stars\n' +
        '  • Blue LED lighting for ambient effect\n' +
        '  • Decorative and functional as a night lamp\n' +
        '  • USB-powered base\n\n' +
        '3. Design:\n' +
        '  • Clear polished glass sphere\n' +
        '  • Wood-grain textured cylindrical base\n' +
        '  • Minimalist, modern look — ideal for bedroom or desk\n\n' +
        '4. Materials:\n' +
        '  • Sphere: K9 crystal or high-grade optical glass\n' +
        '  • Base: Wood or plastic with wood finish\n' +
        '  • Power cable: USB (rubber coated)\n\n' +
        '5. Ports & Controls:\n' +
        '  • Visible USB port for power input\n' +
        '  • No physical buttons shown — likely touch or remote controlled'
    }
  },
  health: {
    prompt: 'Identify the health-related product in the image: function, form, materials, and how it might be used.',
    responses: {
      openai: 
        'This is an orthopedic memory foam pillow designed to support proper neck and spine alignment during sleep.\n\n' +
        '• Function: Ergonomic neck and head support to reduce pressure points and improve spinal alignment\n\n' +
        '• Form: Contoured shape with raised sides and central cradle, ideal for back and side sleepers\n\n' +
        '• Materials: High-density memory foam core with a breathable, mesh-like cover (likely polyester)\n\n' +
        '• Usage: Used at home or in therapy for people with neck pain or posture-related sleep issues',

      gemini: 
        '1. Product: Orthopedic Cervical Pillow\n\n' +
        '2. Function:\n' +
        '  • Aligns head, neck, and spine\n' +
        '  • Reduces pressure and improves sleep posture\n\n' +
        '3. Form:\n' +
        '  • Contoured butterfly shape\n' +
        '  • Central cradle for back sleeping\n' +
        '  • Raised side wings for side sleepers\n\n' +
        '4. Materials:\n' +
        '  • Core: Memory foam or similar supportive material\n' +
        '  • Cover: Hexagonal quilted top + breathable mesh sides\n\n' +
        '5. Usage:\n' +
        '  • Sleep on back with neck supported in center cradle\n' +
        '  • Sleep on side using raised wing for alignment\n' +
        '  • Ideal for users with neck pain, stiffness, or poor posture'
    }
  }
};

export default templateData;
